import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight,
  Zap,
  Clock,
  MessageCircle,
  ChevronDown,
  TrendingUp,
  Target,
  Rocket,
  CheckCircle,
  Gift,
  AlertTriangle,
  Star,
  Calendar,
  Trophy,
  GraduationCap,
  X,
  PlayCircle,
  Sparkles,
  Award,
  Lock,
  ShieldCheck,
  Flame,
} from 'lucide-react';

import StudentResultsSection from '../components/landing/StudentResultsSection';

// Реальные референсы — скриншоты Seller Central
import igorSnapshot from '../assets/igor-snapshot.png';
import alexeyResult from '../assets/alexey-result.png';
import valeriaResult from '../assets/valeria-result.png';
import elenaResult from '../assets/elena-result.png';
import dmitriyResult from '../assets/dmitriy-result.png';
import dariaResult from '../assets/daria-result.png';

const izmailResult = "https://customer-assets.emergentagent.com/job_amazon-course-1/artifacts/kykxsbrv_image.png";

const TELEGRAM_BOT_BASE = "https://t.me/vadjjik1_bot";

// Detect ?start= from URL path slug (/check_lead/tg) or from document.referrer when no slug is set.
// Maps to the bot's start parameters: tg / inst / you / tik / fb
const detectSource = (slug) => {
  const allowed = new Set(['tg', 'inst', 'you', 'tik', 'fb']);
  if (slug && allowed.has(slug)) return slug;
  if (typeof document !== 'undefined' && document.referrer) {
    const ref = document.referrer.toLowerCase();
    if (ref.includes('youtube') || ref.includes('youtu.be')) return 'you';
    if (ref.includes('instagram')) return 'inst';
    if (ref.includes('tiktok')) return 'tik';
    if (ref.includes('facebook') || ref.includes('fb.com') || ref.includes('fb.me')) return 'fb';
    if (ref.includes('t.me') || ref.includes('telegram')) return 'tg';
  }
  return null;
};
const VADIM_PHOTO = "https://customer-assets.emergentagent.com/job_amazon-course-1/artifacts/6vupjb0q_image.png";

// ============== ШКАЛА УРОВНЕЙ — что реально достижимо на Amazon ==============
const levels = [
  {
    id: 1,
    range: '€300 — $1 500',
    period: 'в первую неделю',
    title: 'Первые продажи',
    desc: 'Аккаунт жив, поставщик одобрил, прошла первая партия. Тут многие сливаются — это самый важный шаг.',
    timeline: 'Реалистично за 3–6 недель с нуля',
    color: 'emerald',
    ref: {
      name: 'Дария',
      country: '🇮🇹',
      label: '€485 за день',
      image: dariaResult,
    },
  },
  {
    id: 2,
    range: '$3 000 — $15 000',
    period: 'в месяц',
    title: 'Стабильный поток',
    desc: 'Уже не «эксперимент» — это работающий магазин. Окупает себя, появляется свободный кэш на докупки.',
    timeline: 'Достижимо за 2–4 месяца',
    color: 'amber',
    highlight: true,
    ref: {
      name: 'Игорь',
      country: '🇮🇹',
      label: '$12 386 за месяц',
      detail: '318 продаж • 296 заказов',
      image: igorSnapshot,
      featured: true,
    },
  },
  {
    id: 3,
    range: '$15 000 — $40 000',
    period: 'в месяц',
    title: 'Серьёзный бизнес',
    desc: 'Магазин даёт чистую прибыль, которой хватает на жизнь и реинвест одновременно. Здесь уже подключают помощника.',
    timeline: 'Достижимо за 6–12 месяцев',
    color: 'violet',
    ref: {
      name: 'Валерия',
      country: '🇵🇱',
      label: '€32 348 за месяц',
      image: valeriaResult,
    },
  },
  {
    id: 4,
    range: '$60 000+',
    period: 'в месяц',
    title: 'Высшая лига',
    desc: 'Это уровень топ-учеников. Команда, несколько брендов, оборот вместо «торговли». Не для всех — но достижимо.',
    timeline: 'Год+ работы и реинвеста',
    color: 'rose',
    ref: {
      name: 'Алексей',
      country: '🇺🇸',
      label: '$65 887 за месяц',
      image: alexeyResult,
    },
  },
];

// Дополнительные референсы (для подтверждения)
const extraRefs = [
  { name: 'Измаил',  country: '🇩🇪', label: '€472,956 общий оборот', image: izmailResult },
  { name: 'Дмитрий', country: '🇮🇹', label: '€39,818 за месяц',     image: dmitriyResult },
  { name: 'Елена',   country: '🇮🇹', label: '€10,720 за месяц',     image: elenaResult },
];

// Что внутри Telegram — 3 коротких видео + бонус.
// video: путь до mp4 (H.264, без звука, < 5MB) в /public/videos/. Если null — рендерится placeholder с Play-кнопкой.
const telegramVideos = [
  {
    num: 1,
    title: 'Как Игорь получил первую $1 000',
    sub: 'Пошагово',
    desc: 'Разбор реального пути: что он сделал, с чего начал, какие шаги привели к первым продажам.',
    color: 'amber',
    icon: PlayCircle,
    video: '/videos/case-1.mp4',
  },
  {
    num: 2,
    title: 'Как работает Amazon и как начать',
    sub: 'С нуля',
    desc: 'Простое объяснение модели: где искать товары, как работает продажа, поставщики, закупка и первые действия.',
    color: 'emerald',
    icon: PlayCircle,
    video: '/videos/case-2.mp4',
  },
  {
    num: 3,
    title: 'Почему большинство людей так и не начинают',
    sub: 'Главная причина',
    desc: 'Страхи, ошибки мышления и причины, почему люди годами «изучают», но не запускаются.',
    color: 'blue',
    icon: PlayCircle,
    video: '/videos/case-3.mp4',
  },
];

const colorTokens = {
  emerald: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/40', glow: 'shadow-emerald-500/20', grad: 'from-emerald-500 to-teal-500', dot: 'bg-emerald-400' },
  amber:   { text: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/40',   glow: 'shadow-amber-500/30',   grad: 'from-amber-500 to-orange-500', dot: 'bg-amber-400' },
  violet:  { text: 'text-violet-400',  bg: 'bg-violet-500/10',  border: 'border-violet-500/40',  glow: 'shadow-violet-500/20',  grad: 'from-violet-500 to-purple-500', dot: 'bg-violet-400' },
  rose:    { text: 'text-rose-400',    bg: 'bg-rose-500/10',    border: 'border-rose-500/40',    glow: 'shadow-rose-500/20',    grad: 'from-rose-500 to-pink-500',   dot: 'bg-rose-400' },
  blue:    { text: 'text-blue-400',    bg: 'bg-blue-500/10',    border: 'border-blue-500/40',    glow: 'shadow-blue-500/20',    grad: 'from-blue-500 to-indigo-500',  dot: 'bg-blue-400' },
};

// ============== МОДАЛКА С КАРТИНКОЙ ==============
const ImageModal = ({ image, name, label, onClose }) => {
  if (!image) return null;
  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        type="button"
        className="absolute top-4 right-4 w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center"
        onClick={onClose}
        data-testid="image-modal-close"
      >
        <X className="w-6 h-6 text-white" />
      </button>
      <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
        <img src={image} alt={name} className="w-full max-h-[78vh] object-contain rounded-lg" />
        {label && (
          <div className="mt-4 text-center">
            <p className="text-white font-bold text-lg">{name}</p>
            <p className="text-emerald-400 font-black text-2xl">{label}</p>
          </div>
        )}
      </div>
    </motion.div>,
    document.body
  );
};

// ============== HERO СЕКЦИЯ — кейс Игоря ==============
const IgorHeroCase = ({ onOpenImage }) => (
  <div className="relative">
    {/* Glow behind */}
    <div className="absolute -inset-4 bg-gradient-to-br from-amber-500/20 via-orange-500/10 to-transparent rounded-3xl blur-2xl" aria-hidden />

    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className="relative rounded-3xl bg-zinc-900/80 border border-amber-500/30 overflow-hidden shadow-2xl shadow-amber-500/10"
      data-testid="igor-hero-case"
    >
      {/* Top label */}
      <div className="flex items-center justify-between px-5 sm:px-7 pt-5 sm:pt-6">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-emerald-400 text-xs sm:text-sm font-bold uppercase tracking-wider">Свежий кейс • Март 2026</span>
        </div>
        <span className="text-zinc-500 text-xs sm:text-sm">Скрин из Seller Central</span>
      </div>

      <div className="px-5 sm:px-7 py-4">
        <div className="flex items-baseline gap-2 mb-1">
          <h3 className="text-xl sm:text-2xl font-black text-white">Игорь</h3>
          <span className="text-zinc-500 text-sm">— начал с нуля 2 месяца назад</span>
        </div>
        <p className="text-zinc-300 text-sm sm:text-base">
          Прошёл обучение, нашёл поставщика, отправил первую партию. Это его магазин сегодня:
        </p>
      </div>

      {/* The screenshot itself */}
      <button
        type="button"
        onClick={() => onOpenImage(igorSnapshot, 'Игорь — $12 386 за месяц', '$12 386.49')}
        className="block w-full px-5 sm:px-7 pb-5 group"
        data-testid="igor-snapshot-trigger"
      >
        <div className="relative rounded-xl overflow-hidden border border-zinc-700/60">
          <img
            src={igorSnapshot}
            alt="Sales Snapshot Игоря"
            className="w-full h-auto group-hover:scale-[1.01] transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none" />
          <div className="absolute bottom-3 right-3 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur border border-white/10 text-white text-xs font-medium flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-amber-400" />
            Открыть в полный размер
          </div>
        </div>
      </button>

      {/* Bottom stat strip */}
      <div className="grid grid-cols-3 gap-px bg-zinc-800/80 border-t border-zinc-800">
        <div className="px-3 sm:px-5 py-4 bg-zinc-900/80 text-center">
          <p className="text-emerald-400 text-xl sm:text-3xl font-black tabular-nums">$12 386</p>
          <p className="text-zinc-500 text-[10px] sm:text-xs uppercase tracking-wider mt-0.5">за месяц</p>
        </div>
        <div className="px-3 sm:px-5 py-4 bg-zinc-900/80 text-center">
          <p className="text-amber-400 text-xl sm:text-3xl font-black tabular-nums">318</p>
          <p className="text-zinc-500 text-[10px] sm:text-xs uppercase tracking-wider mt-0.5">продано</p>
        </div>
        <div className="px-3 sm:px-5 py-4 bg-zinc-900/80 text-center">
          <p className="text-blue-400 text-xl sm:text-3xl font-black tabular-nums">296</p>
          <p className="text-zinc-500 text-[10px] sm:text-xs uppercase tracking-wider mt-0.5">заказов</p>
        </div>
      </div>

      <div className="px-5 sm:px-7 py-4 bg-gradient-to-r from-amber-500/5 via-orange-500/10 to-amber-500/5 border-t border-amber-500/20">
        <p className="text-zinc-300 text-sm sm:text-base text-center">
          <Flame className="w-4 h-4 text-amber-400 inline -mt-1 mr-1" />
          Это <span className="text-white font-bold">не топ-результат</span>. Это типичный путь, если делать по системе.
        </p>
      </div>
    </motion.div>
  </div>
);

// ============== LEVEL CARD ==============
const LevelCard = ({ level, onOpenImage }) => {
  const c = colorTokens[level.color];
  const Icon = [Target, TrendingUp, Trophy, Award][level.id - 1] || Target;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.55, delay: level.id * 0.07, ease: [0.16, 1, 0.3, 1] }}
      className="relative"
      data-testid={`level-card-${level.id}`}
    >
      {/* Outer glow for highlighted card */}
      {level.highlight && (
        <div className={`absolute -inset-2 bg-gradient-to-br ${c.grad} opacity-40 rounded-3xl blur-2xl animate-glow-soft pointer-events-none`} aria-hidden />
      )}

      <div className={`relative rounded-2xl overflow-hidden border-2 bg-zinc-900/80 backdrop-blur ${level.highlight ? `${c.border} shadow-2xl ${c.glow}` : 'border-zinc-800/80'} transition-transform ${level.highlight ? 'lg:scale-[1.03]' : ''}`}>
        {/* Top gradient stripe */}
        <div className={`h-1.5 w-full bg-gradient-to-r ${c.grad}`} />

        {/* Highlight pill */}
        {level.highlight && (
          <div className="absolute top-3.5 right-3 flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500 text-black text-[10px] font-black uppercase tracking-wider shadow-lg shadow-amber-500/40 z-10 animate-bounce-soft">
            <Flame className="w-3 h-3" /> Где сейчас Игорь
          </div>
        )}

        {/* Giant level number background */}
        <span
          aria-hidden
          className={`absolute -top-4 -left-2 text-[8rem] sm:text-[9rem] font-black ${c.text} opacity-[0.06] leading-none select-none pointer-events-none`}
        >
          {level.id}
        </span>

        <div className="relative p-5 sm:p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${c.grad} flex items-center justify-center shadow-lg ${c.glow}`}>
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className={`text-[10px] sm:text-xs uppercase tracking-[0.2em] font-bold ${c.text}`}>Уровень {level.id}</p>
              <h3 className="text-lg sm:text-xl font-black text-white leading-tight">{level.title}</h3>
            </div>
          </div>

          <div className="mb-3">
            <p className={`text-2xl sm:text-[2rem] font-black ${c.text} tabular-nums leading-[1.05] tracking-tight`}>{level.range}</p>
            <p className="text-zinc-500 text-sm mt-1">{level.period}</p>
          </div>

          <p className="text-zinc-300 text-sm sm:text-base leading-relaxed mb-4">{level.desc}</p>

          <div className="flex items-center gap-2 mb-4 text-xs text-zinc-500">
            <Clock className="w-3.5 h-3.5" />
            <span>{level.timeline}</span>
          </div>

          {/* Mini reference */}
          {level.ref && (
            <button
              type="button"
              onClick={() => level.ref.image && onOpenImage(level.ref.image, `${level.ref.name} ${level.ref.country}`, level.ref.label)}
              disabled={!level.ref.image}
              className={`w-full p-3 rounded-xl bg-zinc-800/80 border ${c.border.replace('/40', '/30')} flex items-center gap-3 hover:bg-zinc-800 hover:scale-[1.02] transition-all text-left active:scale-[0.98]`}
              data-testid={`level-ref-${level.id}`}
            >
              {level.ref.image && (
                <div className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden border border-zinc-700 flex-shrink-0">
                  <img src={level.ref.image} alt={level.ref.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-0.5 right-0.5 text-xs">{level.ref.country}</div>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-0.5 flex items-center gap-1">
                  <ShieldCheck className={`w-3 h-3 ${c.text}`} /> Реальный ученик
                </p>
                <p className="text-white font-bold text-sm">{level.ref.name}</p>
                <p className={`text-sm font-black ${c.text}`}>{level.ref.label}</p>
                {level.ref.detail && <p className="text-zinc-500 text-xs mt-0.5">{level.ref.detail}</p>}
              </div>
              <ChevronDown className={`w-4 h-4 ${c.text} -rotate-90 flex-shrink-0`} />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// ============== EXTRA REFS STRIP ==============
const ExtraRefsStrip = ({ onOpenImage }) => (
  <div className="space-y-3">
    <p className="text-zinc-500 text-xs sm:text-sm uppercase tracking-wider text-center">
      <ShieldCheck className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />
      Ещё несколько подтверждений
    </p>
    <div className="grid grid-cols-3 gap-2 sm:gap-3">
      {extraRefs.map((r, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onOpenImage(r.image, `${r.name} ${r.country}`, r.label)}
          className="group rounded-xl overflow-hidden bg-zinc-800/60 border border-zinc-700/50 hover:border-emerald-500/40 transition-colors text-left"
          data-testid={`extra-ref-${i}`}
        >
          <div className="relative h-16 sm:h-20 overflow-hidden">
            <img src={r.image} alt={r.name} className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/30 to-transparent" />
            <div className="absolute bottom-1 left-2 text-base">{r.country}</div>
          </div>
          <div className="p-2 sm:p-2.5">
            <p className="text-white text-xs font-bold truncate">{r.name}</p>
            <p className="text-emerald-400 text-[11px] sm:text-xs font-black truncate">{r.label}</p>
          </div>
        </button>
      ))}
    </div>
  </div>
);

// ============== AUTHOR ==============
const AuthorBlock = () => (
  <div className="flex items-center gap-3 p-3 sm:p-4 rounded-2xl bg-zinc-900/70 border border-zinc-800">
    <div className="relative flex-shrink-0">
      <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full blur opacity-60" />
      <img src={VADIM_PHOTO} alt="Вадим" className="relative w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-2 border-amber-500/50" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-white font-bold text-sm sm:text-base">Вадим Малюк <span className="text-amber-400 font-normal">@vadjik</span></p>
      <div className="flex items-center gap-3 mt-1 flex-wrap">
        <span className="flex items-center gap-1 text-xs text-zinc-400"><Calendar className="w-3 h-3 text-amber-400" /> с 2020</span>
        <span className="flex items-center gap-1 text-xs text-zinc-400"><Trophy className="w-3 h-3 text-emerald-400" /> $900K+ оборот</span>
        <span className="flex items-center gap-1 text-xs text-zinc-400"><GraduationCap className="w-3 h-3 text-blue-400" /> 100+ учеников</span>
      </div>
    </div>
  </div>
);

// ============== MAIN ==============
const CheckLeadPage = () => {
  const { source: routeSource } = useParams();
  const [modal, setModal] = useState(null); // {image, name, label}
  const visitIdRef = useRef(null);

  // Resolve traffic source once on mount: URL slug → referrer → none.
  const { telegramUrl, sourceTag } = useMemo(() => {
    const src = detectSource(routeSource);
    return {
      telegramUrl: src ? `${TELEGRAM_BOT_BASE}?start=${src}` : TELEGRAM_BOT_BASE,
      sourceTag: src || 'direct',
    };
  }, [routeSource]);

  // Track visit on mount — send to backend, store visit_id for later "click" event.
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/check-lead/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        source: sourceTag,
        referrer: document.referrer || '',
        user_agent: navigator.userAgent.slice(0, 300),
      }),
    })
      .then((r) => r.json())
      .then((data) => { visitIdRef.current = data.visit_id; })
      .catch(() => {});
  }, [sourceTag]);

  const openImage = (image, name, label) => setModal({ image, name, label });
  const closeImage = () => setModal(null);

  const handleGoToBot = () => {
    // Mark CTA click in the funnel BEFORE opening Telegram
    if (visitIdRef.current) {
      const API = process.env.REACT_APP_BACKEND_URL;
      // Fire-and-forget — don't block the user
      fetch(`${API}/api/check-lead/click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visit_id: visitIdRef.current }),
        keepalive: true,
      }).catch(() => {});
    }
    window.open(telegramUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#08080A]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-[#08080A]/95 backdrop-blur-xl border-b border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-4 h-16 sm:h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <span className="text-black font-black text-lg sm:text-xl">A</span>
            </div>
            <div className="hidden sm:block">
              <span className="text-white font-bold">Amazon</span>
              <span className="text-zinc-400 ml-1">Wholesale</span>
            </div>
          </a>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex flex-col items-end">
              <span className="text-white font-bold text-sm">Вадим Малюк</span>
              <a href="https://t.me/vadjik" className="text-amber-400 text-xs">@vadjik</a>
            </div>
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full blur opacity-50" />
              <img src={VADIM_PHOTO} alt="Вадим" className="relative w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-amber-500/50" />
            </div>
          </div>
        </div>
      </header>

      {/* ============ HERO ============ */}
      <section className="pt-20 sm:pt-28 pb-10 sm:pb-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center mb-5"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs sm:text-sm font-bold">
              <Sparkles className="w-3.5 h-3.5" /> Amazon с Вадимом
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="text-3xl sm:text-5xl lg:text-6xl font-black text-white text-center leading-[1.1] tracking-tight"
          >
            Как начать на Amazon
            <span className="block mt-2">
              и сделать{' '}
              <span className="bg-gradient-to-r from-amber-300 via-orange-400 to-amber-300 bg-clip-text text-transparent">
                первую $1 000
              </span>
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-zinc-300 text-base sm:text-xl text-center max-w-2xl mx-auto mt-4 sm:mt-6 leading-relaxed"
          >
            Без воды и обещаний «миллионов за неделю». Покажу за <span className="text-white font-semibold">3 коротких видео в Telegram</span>:
            живой кейс Игоря, как именно он вышел на <span className="text-white font-semibold">$12 386 за месяц</span> с нуля, и где здесь ваше место.
          </motion.p>

          {/* CTA primary */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.18 }}
            className="mt-7 sm:mt-10 flex flex-col items-center"
          >
            <button
              onClick={handleGoToBot}
              className="group relative inline-flex items-center justify-center gap-3 px-6 sm:px-10 py-4 sm:py-5 rounded-2xl text-base sm:text-lg font-black text-black bg-gradient-to-r from-emerald-400 via-emerald-400 to-teal-400 shadow-2xl shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:scale-[1.02] active:scale-[0.98] transition-all overflow-hidden w-full sm:w-auto max-w-sm"
              data-testid="hero-cta-primary"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              <PlayCircle className="w-5 h-5 sm:w-6 sm:h-6 relative z-10" />
              <span className="relative z-10">Смотреть кейс Игоря в Telegram</span>
              <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 relative z-10 group-hover:translate-x-1 transition-transform" />
            </button>
            <p className="text-zinc-500 text-xs sm:text-sm mt-3 text-center px-2 leading-relaxed">
              Бесплатно · 15 минут · без звонков и спама · сразу в Telegram
            </p>
          </motion.div>

          {/* 3 video previews — sit right under CTA so they're visible on first screen on both desktop and mobile */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            className="mt-7 sm:mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4"
            data-testid="hero-videos-row"
          >
            {telegramVideos.map((v) => {
              const c = colorTokens[v.color];
              const Icon = v.icon;
              return (
                <div
                  key={v.num}
                  className={`relative rounded-2xl overflow-hidden border ${c.border} bg-zinc-900/80 backdrop-blur group`}
                  data-testid={`hero-video-${v.num}`}
                >
                  <div className={`h-1 w-full bg-gradient-to-r ${c.grad}`} />
                  <div className="absolute top-3 right-3 z-10">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-zinc-800/80 ${c.text} border ${c.border.replace('/40', '/30')}`}>
                      Видео {v.num}
                    </span>
                  </div>

                  <div className="p-3 sm:p-4">
                    <div className={`relative w-full aspect-video rounded-lg bg-gradient-to-br from-zinc-800 to-zinc-900 border ${c.border.replace('/40', '/20')} mb-3 flex items-center justify-center overflow-hidden`}>
                      {v.video ? (
                        <>
                          <video
                            autoPlay muted loop playsInline preload="auto"
                            className="absolute inset-0 w-full h-full object-cover"
                            data-testid={`hero-tg-video-bg-${v.num}`}
                          >
                            <source src={v.video.replace('.mp4', '.webm')} type="video/webm" />
                            <source src={v.video} type="video/mp4" />
                          </video>
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent pointer-events-none" />
                        </>
                      ) : (
                        <>
                          <div className={`absolute inset-0 bg-gradient-to-br ${c.grad} opacity-10`} />
                          <div className={`relative w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br ${c.grad} flex items-center justify-center shadow-2xl ${c.glow}`}>
                            <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white ml-0.5" fill="currentColor" />
                          </div>
                        </>
                      )}
                      <div className="absolute bottom-1.5 left-2 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider text-zinc-300 bg-black/50 backdrop-blur-sm px-1.5 py-0.5 rounded">{v.sub}</div>
                    </div>
                    <h3 className="text-white font-black text-sm sm:text-base leading-tight">
                      {v.title}
                    </h3>
                  </div>
                </div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ============ IGOR CASE + AUTHOR ============ */}
      <section className="px-4 sm:px-6 pb-10 sm:pb-16">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[1fr_320px] gap-5 sm:gap-8 items-start">
          <IgorHeroCase onOpenImage={openImage} />
          <div className="space-y-4">
            <AuthorBlock />
            <div className="rounded-2xl p-4 sm:p-5 bg-zinc-900/60 border border-zinc-800">
              <p className="text-zinc-400 text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> Честная пометка
              </p>
              <p className="text-zinc-300 text-sm leading-relaxed">
                Игорь — не «купил курс и через неделю миллион». Это <span className="text-white font-semibold">2 месяца последовательных шагов</span>: поставщик, первая партия, прогон через систему. Точно так же делают остальные.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SCALE OF RESULTS ============ */}
      <section className="relative py-14 sm:py-24 px-4 sm:px-6 overflow-hidden border-y border-zinc-800/50">
        {/* Background flair */}
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-900/40 via-zinc-900/60 to-zinc-900/40 pointer-events-none" aria-hidden />
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-emerald-500/10 blur-[120px] rounded-full pointer-events-none" aria-hidden />
        <div className="absolute -bottom-32 right-0 w-[500px] h-[400px] bg-amber-500/10 blur-[120px] rounded-full pointer-events-none" aria-hidden />

        <div className="relative max-w-6xl mx-auto">
          {/* Section header — much louder */}
          <div className="text-center mb-12 sm:mb-16">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-xs sm:text-sm font-bold uppercase tracking-[0.18em] mb-5 shadow-lg shadow-emerald-500/10"
            >
              <TrendingUp className="w-3.5 h-3.5" />
              Что реально достижимо на Amazon
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.05 }}
              className="text-3xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.05] tracking-tight"
            >
              От <span className="bg-gradient-to-r from-emerald-300 to-emerald-400 bg-clip-text text-transparent">первой $1K</span>
              <br className="hidden sm:block" />
              <span className="sm:hidden"> </span>
              до <span className="bg-gradient-to-r from-amber-300 via-orange-400 to-rose-400 bg-clip-text text-transparent">$60K+</span> в месяц
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-zinc-400 text-base sm:text-lg mt-5 max-w-2xl mx-auto leading-relaxed"
            >
              4 ступени, через которые проходит каждый <span className="text-white font-semibold">человек на Amazon</span>. На каждой — <span className="text-white font-semibold">реальный человек с реальным скриншотом</span>. Нажмите карточку, чтобы убедиться лично.
            </motion.p>

            {/* Progression dots — show the journey visually */}
            <div className="flex items-center justify-center gap-2 sm:gap-3 mt-7">
              {levels.map((l, i) => {
                const c = colorTokens[l.color];
                return (
                  <React.Fragment key={l.id}>
                    <div className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-full bg-zinc-900/60 border ${c.border.replace('/40', '/30')}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                      <span className={`text-[10px] sm:text-xs font-bold ${c.text} tabular-nums`}>{l.range.split('—')[0].trim()}</span>
                    </div>
                    {i < levels.length - 1 && (
                      <ArrowRight className="w-3 h-3 text-zinc-700 flex-shrink-0" />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Levels grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 lg:gap-5">
            {levels.map((l) => (
              <LevelCard key={l.id} level={l} onOpenImage={openImage} />
            ))}
          </div>

          {/* Extra refs */}
          <div className="mt-14 sm:mt-16 max-w-2xl mx-auto">
            <ExtraRefsStrip onOpenImage={openImage} />
          </div>

          {/* Disclaimer */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-10 max-w-2xl mx-auto"
            data-testid="results-disclaimer"
          >
            <div className="flex items-start gap-2.5 px-4 py-3 rounded-xl bg-zinc-900/60 border border-zinc-800/80 text-center sm:text-left">
              <ShieldCheck className="w-4 h-4 text-zinc-500 mt-0.5 flex-shrink-0 hidden sm:block" />
              <p className="text-zinc-500 text-xs sm:text-sm leading-relaxed">
                Это не обещание результата. Скорость зависит от бюджета, времени и действий.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ OTHER EARNERS — StudentResultsSection reused ============ */}
      <StudentResultsSection
        badge="ОНИ УЖЕ НАЧАЛИ"
        titleA="Узнай"
        titleB=" как другие зарабатывают на Amazon"
        subtitleLead=""
        subtitleEmphasis="Реальные продавцы — реальные цифры"
        description={
          <>
            <span className="text-emerald-400 font-bold">Скриншоты из Seller Central</span> — нажмите на любую карточку, чтобы увидеть детали
          </>
        }
        ctaLabel="Смотреть кейс Игоря в Telegram"
        ctaHref={telegramUrl}
        ctaOnClick={(e) => { e.preventDefault(); handleGoToBot(); }}
      />

      {/* ============ BONUS CARD ============ */}
      <section className="relative py-12 sm:py-16 px-4 sm:px-6 overflow-hidden">
        <div className="absolute -bottom-20 left-0 w-[500px] h-[400px] bg-amber-500/10 blur-[120px] rounded-full pointer-events-none" aria-hidden />

        <div className="relative max-w-4xl mx-auto">
          {/* BONUS card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
            data-testid="tg-bonus"
          >
            {/* Outer animated glow */}
            <div className="absolute -inset-2 bg-gradient-to-r from-amber-500/40 via-orange-500/30 to-amber-500/40 rounded-3xl blur-2xl animate-glow-soft pointer-events-none" aria-hidden />

            <div className="relative rounded-2xl overflow-hidden border-2 border-amber-500/50 bg-gradient-to-br from-amber-500/10 via-zinc-900/95 to-orange-500/10 shadow-2xl shadow-amber-500/20">
              <div className="absolute top-0 right-0 px-4 py-1.5 bg-gradient-to-l from-amber-500 to-orange-500 text-black text-[10px] sm:text-xs font-black uppercase tracking-wider rounded-bl-2xl flex items-center gap-1.5 shadow-lg">
                <Gift className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> Бонус
              </div>

              <div className="p-5 sm:p-8 flex items-start gap-4 sm:gap-6">
                <div className="hidden sm:flex flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 items-center justify-center shadow-xl shadow-amber-500/30">
                  <AlertTriangle className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 sm:hidden mb-2">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
                      <AlertTriangle className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <h3 className="text-white text-xl sm:text-2xl font-black leading-tight mb-2 sm:mb-3 pr-16 sm:pr-0">
                    Топ-3 ошибки Amazon-продавца
                  </h3>
                  <p className="text-zinc-300 text-sm sm:text-base leading-relaxed">
                    Ошибки, которые я чаще всего видел за <span className="text-amber-300 font-bold">6 лет опыта</span> — и которые могут привести к потере всех денег.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="py-14 sm:py-24 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-4xl font-black text-white leading-tight mb-7 sm:mb-9">
            15 минут — и вы поймёте,<br />
            <span className="text-amber-400">ваше это или нет</span>
          </h2>
          <button
            onClick={handleGoToBot}
            className="group relative inline-flex items-center justify-center gap-3 px-7 sm:px-12 py-4 sm:py-5 rounded-2xl text-base sm:text-lg font-black text-black bg-gradient-to-r from-emerald-400 to-teal-400 shadow-2xl shadow-emerald-500/30 hover:scale-[1.02] active:scale-[0.98] transition-all w-full sm:w-auto max-w-md"
            data-testid="final-cta"
          >
            <PlayCircle className="w-5 h-5 sm:w-6 sm:h-6" />
            <span>Смотреть кейс Игоря в Telegram</span>
            <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-1 transition-transform" />
          </button>
          <p className="text-zinc-500 text-xs sm:text-sm mt-4 px-2 leading-relaxed">
            Бесплатно · 15 минут · без звонков и спама · сразу в Telegram
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 px-4 border-t border-zinc-800/50">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
          <span className="text-zinc-600">© 2026 Вадим Малюк</span>
          <a href="https://t.me/vadjik" className="text-zinc-500 hover:text-amber-400 flex items-center gap-1.5">
            <MessageCircle className="w-4 h-4" /> @vadjik
          </a>
        </div>
      </footer>

      <AnimatePresence>
        {modal && (
          <ImageModal
            image={modal.image}
            name={modal.name}
            label={modal.label}
            onClose={closeImage}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default CheckLeadPage;
